import { type FormEvent, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Search } from 'lucide-react'
import AdminNav from '../../components/admin/AdminNav'
import ProviderTable from '../../components/admin/ProviderTable'
import Button from '../../components/ui/Button'
import { useLang } from '../../contexts/LangContext'
import {
  ADMIN_PROVIDERS_QUERY,
  type AdminProvidersData,
  type AdminProvidersVariables,
} from '../../graphql/admin'
import { normalizeLocale } from '../../lib/i18n'

const PROVIDERS_PAGE_SIZE = 12
const STATUSES = [ 'all', 'pending', 'approved', 'suspended' ] as const

function normalizeStatus(value: string | null) {
  return STATUSES.includes((value ?? 'all') as typeof STATUSES[number]) ? (value ?? 'all') : 'all'
}

function normalizePage(value: string | null) {
  const parsed = Number.parseInt(value ?? '1', 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

export default function AdminProvidersPage() {
  const { locale, t } = useLang()
  const activeLocale = normalizeLocale(locale)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const status = normalizeStatus(searchParams.get('status'))
  const search = searchParams.get('search') ?? ''
  const page = normalizePage(searchParams.get('page'))
  const [draftSearch, setDraftSearch] = useState(search)

  useEffect(() => {
    setDraftSearch(search)
  }, [search])

  const { data, previousData, loading, error, refetch } = useQuery<AdminProvidersData, AdminProvidersVariables>(ADMIN_PROVIDERS_QUERY, {
    variables: {
      status: status === 'all' ? undefined : status,
      search: search || undefined,
      page,
      perPage: PROVIDERS_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  })

  const providersResult = data?.adminProviders ?? previousData?.adminProviders

  function updateParams(updates: { page?: number | null; search?: string | null; status?: string | null }) {
    const next = new URLSearchParams(searchParams)

    Object.entries(updates).forEach(([ key, value ]) => {
      if (value === null || value === undefined || value === '' || (key === 'page' && value === 1)) {
        next.delete(key)
      } else {
        next.set(key, String(value))
      }
    })

    setSearchParams(next)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    updateParams({ search: draftSearch.trim() || null, page: 1 })
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4" />
        {t('Επιστροφή στο dashboard', 'Back to dashboard')}
      </Link>

      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
          {t('Διαχείριση παρόχων', 'Provider operations')}
        </p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
          {t('Έγκριση, αναζήτηση και έλεγχος παρόχων', 'Approve, search, and review providers')}
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
          {t(
            'Φίλτραρε με βάση verification status, ψάξε με όνομα ή email και άνοιξε κάθε προφίλ για πλήρες review και actions.',
            'Filter by verification status, search by name or email, and open each profile for full review and actions.',
          )}
        </p>
      </section>

      <AdminNav />

      <section className="mt-8 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {STATUSES.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => updateParams({ status: item === 'all' ? null : item, page: 1 })}
                className={[
                  'rounded-full border px-4 py-2 text-sm font-semibold transition',
                  status === item
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                ].join(' ')}
              >
                {item === 'all' ? t('Όλοι', 'All') : item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>

          <p className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600">
            {providersResult?.totalCount ?? 0} {t('σύνολο', 'total')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 md:flex-row">
          <label className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
              placeholder={t('Αναζήτηση με όνομα ή email', 'Search by name or email')}
              className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
            />
          </label>
          <Button type="submit">{t('Εφαρμογή', 'Apply')}</Button>
          {search ? (
            <Button type="button" variant="secondary" onClick={() => { setDraftSearch(''); updateParams({ search: null, page: 1 }) }}>
              {t('Καθαρισμός', 'Clear')}
            </Button>
          ) : null}
        </form>
      </section>

      {error ? (
        <div className="mt-6 rounded-[1.5rem] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          <p>{t('Δεν ήταν δυνατή η φόρτωση των παρόχων.', 'The provider list could not be loaded.')}</p>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              {t('Δοκίμασε ξανά', 'Try again')}
            </Button>
          </div>
        </div>
      ) : null}

      {loading && !providersResult ? (
        <p className="mt-8 text-sm text-slate-500">{t('Φορτώνουμε τους παρόχους...', 'Loading providers...')}</p>
      ) : (
        <div className="mt-8">
          <ProviderTable
            providers={providersResult?.nodes ?? []}
            locale={activeLocale}
            emptyMessage={t('Δεν βρέθηκαν πάροχοι για αυτό το φίλτρο.', 'No providers matched this filter.')}
            onSelectProvider={(providerId) => {
              navigate(`/admin/providers/${providerId}`, {
                state: { backTo: `${location.pathname}${location.search}` },
              })
            }}
          />
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {t('Σελίδα', 'Page')} {page}
        </p>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => updateParams({ page: page - 1 })}
          >
            {t('Προηγούμενη', 'Previous')}
          </Button>
          <Button
            type="button"
            disabled={!providersResult?.pageInfo.hasNextPage}
            onClick={() => updateParams({ page: page + 1 })}
          >
            {t('Επόμενη', 'Next')}
          </Button>
        </div>
      </div>
    </div>
  )
}
